import { useState, useEffect, useCallback } from "react";
import { AxiosError } from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { FieldError } from "@/components/common/FieldError";
import { AddressSection } from "./AddressSection";
import { ImageUpload } from "@/components/shared/ImageUpload";
import { ImageGallery } from "@/components/shared/ImageGallery";
import { ErrorAlert } from "@/components/common/ErrorAlert";
import { DuplicateWarningDialog } from "./DuplicateWarningDialog";
import { CloseLeadDialog, type CloseType } from "./CloseLeadDialog";
import { JobArchiveDialog } from "./JobArchiveDialog";
import {
  createLead,
  updateLead,
  transitionLeadStatus,
} from "../api/leads-api";
import { getInsuranceCompanyById } from "@/features/admin/api/insurance";
import { useReferenceData } from "../hooks/useReferenceData";
import { useLeadImages } from "../hooks/useLeadImages";
import { useUploadLeadImages } from "../hooks/useUploadLeadImages";
import { useDuplicateCheck } from "../hooks/useDuplicateCheck";
import { useAuth } from "@/hooks/useAuth";
import { Permissions } from "@/types";
import { parseValidationErrors, type ValidationErrors, cn } from "@/lib/utils";
import type { Lead, LeadFormData, LeadStatus, InsuranceCompanyReference } from "../types";
import type { PotentialDuplicate } from "../api/checkDuplicates";

interface LeadFormProps {
  lead?: Lead | null;
  onSuccess: (lead: Lead) => void;
  onCancel?: () => void;
  readOnly?: boolean;
}

const emptyFormData: LeadFormData = {
  firstName: "",
  lastName: "",
  companyName: "",
  homePhone: "",
  cellPhone: "",
  workPhone: "",
  otherPhone: "",
  homePhoneNotes: "",
  cellPhoneNotes: "",
  workPhoneNotes: "",
  otherPhoneNotes: "",
  email: "",
  address1: "",
  address2: "",
  city: "",
  state: "OH",
  zip: "",
  hasSameAddress: true,
  billingAddress1: "",
  billingAddress2: "",
  billingCity: "",
  billingState: "",
  billingZip: "",
  tenantName: "",
  tenantHomePhone: "",
  tenantCellPhone: "",
  tenantWorkPhone: "",
  tenantOtherPhone: "",
  tenantHomePhoneNotes: "",
  tenantCellPhoneNotes: "",
  tenantWorkPhoneNotes: "",
  tenantOtherPhoneNotes: "",
  status: "Unassigned",
  commissionRate: 0,
  assignedToUserId: undefined,
  insuranceCompanyId: undefined,
  adjusterName: "",
  claimNumber: "",
  claimDate: "",
  damageType: undefined,
  damageDescription: "",
  damageDate: "",
  referralSource: "",
  referralOther: "",
  closingComments: "",
  notes: "",
  directionsComments: "",
};

export function LeadForm({ lead, onSuccess, readOnly = false }: LeadFormProps) {
  const [formData, setFormData] = useState<LeadFormData>(emptyFormData);
  const [originalStatus, setOriginalStatus] = useState<LeadStatus | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] =
    useState<ValidationErrors | null>(null);

  // Duplicate detection state
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicates, setDuplicates] = useState<PotentialDuplicate[]>([]);
  const [duplicatesAcknowledged, setDuplicatesAcknowledged] = useState(false);

  // Close lead dialog state
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [selectedCloseType, setSelectedCloseType] = useState<CloseType | null>(
    null,
  );

  // Job archive dialog state (for Sold → Assigned transition)
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);

  const {
    users,
    insuranceCompanies,
    referralSources,
    damageCategories,
    isLoading,
  } = useReferenceData();
  const { checkForDuplicates, isChecking } = useDuplicateCheck();
  const { hasPermission } = useAuth();

  // Image hooks - only active when editing an existing lead
  const leadId = lead && lead.id > 0 ? lead.id : 0;
  const {
    images,
    isLoading: imagesLoading,
    error: fetchImageError,
    refetch: refetchImages,
    deleteImage,
  } = useLeadImages(leadId);
  const {
    uploadImages,
    isUploading,
    error: uploadImageError,
  } = useUploadLeadImages(leadId, refetchImages);

  // Check if user can view/edit commissions
  const canViewCommission = hasPermission(Permissions.CommissionsRead);

  // Merge inactive insurance company into dropdown list when editing a lead
  const [mergedInsuranceCompanies, setMergedInsuranceCompanies] =
    useState<InsuranceCompanyReference[]>(insuranceCompanies);

  useEffect(() => {
    if (
      !lead?.insuranceCompanyId ||
      insuranceCompanies.length === 0
    ) {
      setMergedInsuranceCompanies(insuranceCompanies);
      return;
    }

    const found = insuranceCompanies.some(
      (c) => c.id === lead.insuranceCompanyId
    );
    if (found) {
      setMergedInsuranceCompanies(insuranceCompanies);
      return;
    }

    // Fetch the inactive company by ID and merge it in (sorted alphabetically)
    getInsuranceCompanyById(lead.insuranceCompanyId)
      .then((company) => {
        const merged = [
          ...insuranceCompanies,
          { id: company.id, name: company.name },
        ].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
        setMergedInsuranceCompanies(merged);
      })
      .catch(() => {
        // If fetch fails, just use the active list
        setMergedInsuranceCompanies(insuranceCompanies);
      });
  }, [lead, insuranceCompanies]);

  // Helper to get error for a specific field
  const getFieldError = (fieldName: string): string | undefined => {
    return validationErrors?.fieldErrors[fieldName]?.[0];
  };

  const statuses: LeadStatus[] = [
    "Unassigned",
    "Assigned",
    "Sold",
    "NoGo",
    "Closed",
  ];

  // Initialize form with lead data if editing
  useEffect(() => {
    if (lead) {
      setFormData({
        firstName: lead.firstName,
        lastName: lead.lastName,
        companyName: lead.companyName || "",
        homePhone: lead.homePhone || "",
        cellPhone: lead.cellPhone || "",
        workPhone: lead.workPhone || "",
        otherPhone: lead.otherPhone || "",
        homePhoneNotes: lead.homePhoneNotes || "",
        cellPhoneNotes: lead.cellPhoneNotes || "",
        workPhoneNotes: lead.workPhoneNotes || "",
        otherPhoneNotes: lead.otherPhoneNotes || "",
        email: lead.email || "",
        address1: lead.address1,
        address2: lead.address2 || "",
        city: lead.city,
        state: lead.state,
        zip: lead.zip,
        hasSameAddress: lead.hasSameAddress,
        billingAddress1: lead.billingAddress1 || "",
        billingAddress2: lead.billingAddress2 || "",
        billingCity: lead.billingCity || "",
        billingState: lead.billingState || "",
        billingZip: lead.billingZip || "",
        tenantName: lead.tenantName || "",
        tenantHomePhone: lead.tenantHomePhone || "",
        tenantCellPhone: lead.tenantCellPhone || "",
        tenantWorkPhone: lead.tenantWorkPhone || "",
        tenantOtherPhone: lead.tenantOtherPhone || "",
        tenantHomePhoneNotes: lead.tenantHomePhoneNotes || "",
        tenantCellPhoneNotes: lead.tenantCellPhoneNotes || "",
        tenantWorkPhoneNotes: lead.tenantWorkPhoneNotes || "",
        tenantOtherPhoneNotes: lead.tenantOtherPhoneNotes || "",
        status: lead.status,
        commissionRate: lead.commissionRate ?? undefined,
        assignedToUserId: lead.assignedToUserId,
        insuranceCompanyId: lead.insuranceCompanyId,
        adjusterName: lead.adjusterName || "",
        claimNumber: lead.claimNumber || "",
        claimDate: lead.claimDate || "",
        damageType: lead.damageType,
        damageDescription: lead.damageDescription || "",
        damageDate: lead.damageDate || "",
        referralSource: lead.referralSource || "",
        referralOther: lead.referralOther || "",
        closingComments: lead.closingComments || "",
        notes: lead.notes || "",
        directionsComments: lead.directionsComments || "",
      });
      // Track original status for detecting status changes
      setOriginalStatus(lead.status);
    } else {
      // Reset form and status when creating a new lead
      setFormData(emptyFormData);
      setOriginalStatus(null);
    }
  }, [lead]);

  const handleChange = (
    field: keyof LeadFormData,
    value: string | number | boolean | undefined,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Reset duplicates acknowledged when form data changes
    setDuplicatesAcknowledged(false);
  };

  // Function to perform the actual save (with status transition support)
  const performSave = useCallback(async () => {
    setIsSubmitting(true);

    try {
      let savedLead: Lead;
      if (lead) {
        // First, update all the non-status fields
        savedLead = await updateLead(lead.id, formData);

        // Then, if status has changed, use the status transition API
        const statusChanged =
          originalStatus && formData.status !== originalStatus;
        if (statusChanged) {
          // For NoGo and Closed statuses, include closing comments
          const needsClosingComments =
            formData.status === "NoGo" || formData.status === "Closed";
          const comments = needsClosingComments
            ? formData.closingComments
            : undefined;

          savedLead = await transitionLeadStatus(
            lead.id,
            formData.status,
            comments,
          );
        }
      } else {
        savedLead = await createLead(formData);
      }
      onSuccess(savedLead);
    } catch (err) {
      // Parse ASP.NET validation errors
      const parsedErrors = parseValidationErrors(err as AxiosError);
      setValidationErrors(parsedErrors);
    } finally {
      setIsSubmitting(false);
    }
  }, [lead, formData, onSuccess, originalStatus]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationErrors(null);

    // Client-side validation
    const clientErrors: ValidationErrors = {
      fieldErrors: {},
      generalErrors: [],
    };

    if (!formData.firstName.trim()) {
      clientErrors.fieldErrors.firstName = ["First name is required"];
    }
    if (!formData.lastName.trim()) {
      clientErrors.fieldErrors.lastName = ["Last name is required"];
    }
    if (!formData.address1.trim()) {
      clientErrors.fieldErrors.address1 = ["Property address is required"];
    }
    if (!formData.city.trim()) {
      clientErrors.fieldErrors.city = ["City is required"];
    }
    if (!formData.state.trim()) {
      clientErrors.fieldErrors.state = ["State is required"];
    }
    if (!formData.zip.trim()) {
      clientErrors.fieldErrors.zip = ["Zip code is required"];
    }

    // If there are client-side validation errors, show them
    if (Object.keys(clientErrors.fieldErrors).length > 0) {
      setValidationErrors(clientErrors);
      return;
    }

    // Skip duplicate check if user already acknowledged duplicates
    if (duplicatesAcknowledged) {
      await performSave();
      return;
    }

    // Only check for duplicates when creating a new lead (not when updating)
    if (!lead) {
      // Check for duplicates before saving
      // Note: Backend expects PropertyAddress1/PropertyCity/PropertyZip naming
      const duplicateResult = await checkForDuplicates({
        firstName: formData.firstName,
        lastName: formData.lastName,
        propertyAddress1: formData.address1,
        propertyCity: formData.city,
        propertyZip: formData.zip,
        homePhone: formData.homePhone,
        cellPhone: formData.cellPhone,
        workPhone: formData.workPhone,
        email: formData.email,
      });

      if (duplicateResult.hasDuplicates) {
        setDuplicates(duplicateResult.duplicates);
        setShowDuplicateDialog(true);
        return;
      }
    }

    // No duplicates found (or updating existing lead), proceed with save
    await performSave();
  };

  // Handler for proceeding after duplicate warning
  const handleProceedWithDuplicates = async () => {
    setDuplicatesAcknowledged(true);
    setShowDuplicateDialog(false);
    await performSave();
  };

  // Handler for canceling after duplicate warning
  const handleCancelDuplicates = () => {
    setShowDuplicateDialog(false);
  };

  // Handler for close type selection
  const handleCloseTypeChange = (closeType: string) => {
    if (closeType === "NoGo" || closeType === "Closed") {
      setSelectedCloseType(closeType as CloseType);
      setShowCloseDialog(true);
    }
  };

  // Handler for confirming close with reason
  const handleCloseConfirm = async (closeType: CloseType, reason: string) => {
    // Update form data with the close type and reason
    setFormData((prev) => ({
      ...prev,
      status: closeType,
      closingComments: reason,
    }));

    setShowCloseDialog(false);
    setSelectedCloseType(null);

    // If editing an existing lead, trigger the status transition immediately
    if (lead?.id) {
      setIsSubmitting(true);
      try {
        const savedLead = await transitionLeadStatus(
          lead.id,
          closeType,
          reason,
        );
        onSuccess(savedLead);
      } catch (err) {
        const parsedErrors = parseValidationErrors(err as AxiosError);
        setValidationErrors(parsedErrors);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // Handler for confirming Sold → Assigned with archive choice
  const handleArchiveConfirm = async (archiveJob: boolean) => {
    setShowArchiveDialog(false);

    if (lead?.id) {
      setIsSubmitting(true);
      try {
        const savedLead = await transitionLeadStatus(
          lead.id,
          "Assigned",
          undefined,
          archiveJob,
        );
        onSuccess(savedLead);
      } catch (err) {
        const parsedErrors = parseValidationErrors(err as AxiosError);
        setValidationErrors(parsedErrors);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  if (isLoading) {
    return <div className="p-6">Loading reference data...</div>;
  }

  return (
    <form onSubmit={handleSubmit} className="bg-card rounded-[10px] p-5 space-y-1">
      <fieldset disabled={readOnly} className="space-y-1">
        {validationErrors &&
          (validationErrors.generalErrors.length > 0 ||
            Object.keys(validationErrors.fieldErrors).length > 0) && (
            <div className="rounded-lg bg-red-50 p-2 border border-red-200">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-4 w-4 text-red-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-2">
                  <h3 className="text-xs font-medium text-red-800">
                    {Object.keys(validationErrors.fieldErrors).length > 0
                      ? "Please correct the following errors:"
                      : "Error:"}
                  </h3>
                  <div className="mt-1 text-xs text-red-700">
                    {validationErrors.generalErrors.length > 0 && (
                      <ul className="list-disc pl-4 space-y-0.5">
                        {validationErrors.generalErrors.map((error, index) => (
                          <li key={`general-${index}`}>{error}</li>
                        ))}
                      </ul>
                    )}
                    {Object.keys(validationErrors.fieldErrors).length > 0 && (
                      <ul className="list-disc pl-4 space-y-0.5 mt-1">
                        {Object.entries(validationErrors.fieldErrors).map(
                          ([field, messages]) => {
                            const displayField =
                              field.charAt(0).toUpperCase() +
                              field.slice(1).replace(/([A-Z])/g, " $1");
                            return messages.map((message, index) => (
                              <li key={`${field}-${index}`}>
                                <strong>{displayField}:</strong> {message}
                              </li>
                            ));
                          },
                        )}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

        {/* Lead Info Row - outside main container */}
        <div className="px-2 py-1">
          <div className="grid grid-cols-5 gap-2">
            <div>
              <Label htmlFor="jobNumber" className="text-[10px]">
                Job Number
              </Label>
              <Input
                id="jobNumber"
                value={lead?.jobNumber || "Auto-generated"}
                disabled
                className="h-6 text-xs"
              />
            </div>
            <div>
              <Label htmlFor="leadDate" className="text-[10px]">
                Lead Date
              </Label>
              <Input
                id="leadDate"
                value={
                  lead?.createdAt
                    ? new Date(lead.createdAt).toLocaleDateString()
                    : "Today"
                }
                disabled
                className="h-6 text-xs"
              />
            </div>
            <div>
              <Label htmlFor="assignedToUserId" className="text-[10px]">
                Job Estimator
              </Label>
              <Select
                id="assignedToUserId"
                value={formData.assignedToUserId?.toString() || ""}
                onChange={(e) => {
                  const userId = e.target.value ? Number(e.target.value) : undefined;
                  handleChange("assignedToUserId", userId);
                  const selectedUser = users.find((u) => u.id === userId);
                  if (selectedUser) {
                    handleChange("commissionRate", selectedUser.commissionRate * 100);
                  }
                }}
                className="h-6 text-xs"
              >
                <option value="">Unassigned</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </Select>
            </div>
            {canViewCommission && (
              <div>
                <Label htmlFor="commissionRate" className="text-[10px]">
                  Commission %
                </Label>
                <Input
                  id="commissionRate"
                  type="number"
                  step="1"
                  value={formData.commissionRate}
                  onChange={(e) =>
                    handleChange(
                      "commissionRate",
                      parseFloat(e.target.value) || 0,
                    )
                  }
                  className="h-6 text-xs"
                />
              </div>
            )}
            <div>
              <Label htmlFor="status" className="text-[10px]">
                Status
              </Label>
              <Select
                id="status"
                value={formData.status}
                onChange={(e) => {
                  const newStatus = e.target.value as LeadStatus;
                  // For close-type statuses on existing leads, show dialog to get reason
                  if (
                    lead?.id &&
                    (newStatus === "NoGo" || newStatus === "Closed")
                  ) {
                    handleCloseTypeChange(newStatus);
                  } else if (
                    lead?.id &&
                    lead.status === "Sold" &&
                    newStatus === "Assigned"
                  ) {
                    // Sold → Assigned: show archive dialog
                    setShowArchiveDialog(true);
                  } else {
                    handleChange("status", newStatus);
                  }
                }}
                className="h-6 text-xs"
              >
                {statuses.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        </div>

        {/* Main Form Container */}
        <div className="p-2">
          {/* Customer Information Section */}
          <div className="mb-1.5">
            <h3 className="text-secondary font-semibold text-xs mb-1">Customer Information</h3>
            <div className="grid grid-cols-4 gap-2">
              <div>
                <Label className="text-[10px]">Lead Title</Label>
                <div className="h-6 text-xs flex items-center px-2 bg-muted rounded-md text-muted-foreground truncate">
                  {(() => {
                    const baseName = formData.companyName?.trim()
                      || `${formData.lastName?.trim() || ''}, ${formData.firstName?.trim() || ''}`;
                    const damagePart = formData.damageType?.trim() ? ` - ${formData.damageType.trim()}` : '';
                    const title = `${baseName}${damagePart}`;
                    return title === ', ' ? 'Auto-generated from name and damage type' : title;
                  })()}
                </div>
              </div>
              <div>
                <Label htmlFor="firstName" className="text-[10px]">First Name *</Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  required
                  autoFocus={!lead}
                  className={cn("h-6 text-xs", getFieldError('firstName') && 'border-red-500')}
                />
                <FieldError error={getFieldError('firstName')} />
              </div>
              <div>
                <Label htmlFor="lastName" className="text-[10px]">Last Name *</Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  required
                  className={cn("h-6 text-xs", getFieldError('lastName') && 'border-red-500')}
                />
                <FieldError error={getFieldError('lastName')} />
              </div>
              <div>
                <Label htmlFor="companyName" className="text-[10px]">Company</Label>
                <Input
                  id="companyName"
                  value={formData.companyName || ''}
                  onChange={(e) => handleChange('companyName', e.target.value)}
                  className="h-6 text-xs"
                />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-1">
              <Checkbox
                id="hasSameAddress"
                checked={formData.hasSameAddress}
                onChange={(e) => handleChange('hasSameAddress', e.target.checked)}
              />
              <Label htmlFor="hasSameAddress" className="cursor-pointer text-[10px]">
                Addresses Are The Same
              </Label>
            </div>
          </div>

          {/* Address Section - 4 columns */}
          <AddressSection formData={formData} onChange={handleChange} />

          {/* Bottom Section - 3 columns */}
          <div className="grid grid-cols-3 gap-2 mt-1.5">
            {/* Job Details */}
            <div>
              <h3 className="text-secondary font-semibold text-xs mb-1">
                Job Details
              </h3>
              <div className="space-y-1">
                <div>
                  <Label htmlFor="damageType" className="text-[10px]">
                    Damage Category
                  </Label>
                  <Select
                    id="damageType"
                    value={formData.damageType || ""}
                    onChange={(e) =>
                      handleChange(
                        "damageType",
                        e.target.value || undefined,
                      )
                    }
                    className="h-6 text-xs"
                  >
                    <option value="">Select Category</option>
                    {damageCategories.map((category) => (
                      <option key={category.id} value={category.name}>
                        {category.name}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <Label htmlFor="damageDescription" className="text-[10px]">
                    Damage Description
                  </Label>
                  <Textarea
                    id="damageDescription"
                    value={formData.damageDescription || ""}
                    onChange={(e) =>
                      handleChange("damageDescription", e.target.value)
                    }
                    rows={5}
                    className="text-xs py-1 resize-none overflow-y-auto"
                  />
                </div>
                <div>
                  <Label htmlFor="directionsComments" className="text-[10px]">
                    Directions/Comments
                  </Label>
                  <Textarea
                    id="directionsComments"
                    value={formData.directionsComments || ""}
                    onChange={(e) =>
                      handleChange("directionsComments", e.target.value)
                    }
                    rows={5}
                    className="text-xs py-1 resize-none overflow-y-auto"
                  />
                </div>
              </div>
            </div>

            {/* Insurance Information */}
            <div>
              <h3 className="text-secondary font-semibold text-xs mb-1">
                Insurance Information
              </h3>
              <div className="space-y-1">
                <div>
                  <Label htmlFor="insuranceCompanyId" className="text-[10px]">
                    Company
                  </Label>
                  <Select
                    id="insuranceCompanyId"
                    value={formData.insuranceCompanyId?.toString() || ""}
                    onChange={(e) => {
                      const value = e.target.value
                        ? Number(e.target.value)
                        : undefined;
                      handleChange("insuranceCompanyId", value);
                    }}
                    className="h-6 text-xs"
                  >
                    <option value="">Select Company</option>
                    {mergedInsuranceCompanies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <Label
                    htmlFor="adjusterName"
                    className="text-[10px]"
                  >
                    Adjustor
                  </Label>
                  <Input
                    id="adjusterName"
                    value={formData.adjusterName || ""}
                    onChange={(e) =>
                      handleChange("adjusterName", e.target.value)
                    }
                    className="h-6 text-xs"
                  />
                </div>
                <div className="grid grid-cols-2 gap-1">
                  <div>
                    <Label htmlFor="claimNumber" className="text-[10px]">
                      Claim/Reference #
                    </Label>
                    <Input
                      id="claimNumber"
                      value={formData.claimNumber || ""}
                      onChange={(e) =>
                        handleChange("claimNumber", e.target.value)
                      }
                      className="h-6 text-xs"
                    />
                  </div>
                  <div>
                    <Label htmlFor="claimDate" className="text-[10px]">
                      Date of Loss
                    </Label>
                    <Input
                      id="claimDate"
                      type="date"
                      value={formData.claimDate || ""}
                      onChange={(e) =>
                        handleChange("claimDate", e.target.value)
                      }
                      className={cn(
                        "h-6 text-xs",
                        getFieldError?.("claimDate") && "border-red-500",
                      )}
                    />
                    <FieldError error={getFieldError?.("claimDate")} />
                  </div>
                </div>
                {/* Image Upload */}
                {leadId > 0 && (
                  <div className="mt-1">
                    <h3 className="text-secondary font-semibold text-xs mb-1">Upload Images</h3>
                    <ImageUpload
                      onUpload={uploadImages}
                      isUploading={isUploading}
                      maxFiles={10}
                      maxSizeMB={10}
                      compact
                    />
                    <ErrorAlert error={uploadImageError} className="mt-1" />
                  </div>
                )}
              </div>
            </div>

            {/* Referral Source & Closing */}
            <div className="space-y-1.5">
              {/* Referral Source */}
              <div>
                <h3 className="text-secondary font-semibold text-xs mb-1">
                  Referral Source
                </h3>
                <div className="grid grid-cols-2 gap-1">
                  <div>
                    <Label htmlFor="referralSource" className="text-[10px]">
                      Referred By
                    </Label>
                    <Select
                      id="referralSource"
                      value={formData.referralSource || ""}
                      onChange={(e) =>
                        handleChange("referralSource", e.target.value)
                      }
                      className="h-6 text-xs"
                    >
                      <option value="">Select Source</option>
                      {referralSources.map((source) => (
                        <option key={source.id} value={source.name}>
                          {source.name}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="referralOther" className="text-[10px]">
                      Other
                    </Label>
                    <Input
                      id="referralOther"
                      value={formData.referralOther || ""}
                      onChange={(e) =>
                        handleChange("referralOther", e.target.value)
                      }
                      className="h-6 text-xs"
                    />
                  </div>
                </div>
              </div>

              {/* Closing */}
              <div>
                <h3 className="text-secondary font-semibold text-xs mb-1">
                  Closing
                </h3>
                <div className="grid grid-cols-2 gap-1">
                  <div>
                    <Label htmlFor="closeType" className="text-[10px]">
                      Close Type
                    </Label>
                    <Select
                      id="closeType"
                      value=""
                      onChange={(e) => handleCloseTypeChange(e.target.value)}
                      className="h-6 text-xs"
                    >
                      <option value="">Select</option>
                      <option value="NoGo">No Go</option>
                      <option value="Closed">Closed</option>
                    </Select>
                  </div>
                  {lead?.closingComments && (
                    <div className="col-span-2">
                      <Label className="text-[10px]">Reason for Closing</Label>
                      <div className="px-1 py-0.5 bg-gray-50 rounded text-[10px] text-gray-700 max-h-10 overflow-y-auto">
                        {lead.closingComments}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* Image Gallery */}
              {leadId > 0 && (
                <div className="mt-1">
                  <h3 className="text-secondary font-semibold text-xs mb-1">
                    Images ({imagesLoading ? "..." : images.length})
                  </h3>
                  <ErrorAlert error={fetchImageError} className="mb-1" />
                  <ImageGallery
                    images={images}
                    onDelete={!readOnly ? deleteImage : undefined}
                    canDelete={!readOnly}
                    compact
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </fieldset>

      {/* Save Button - Centered (only show when not read-only) */}
      {!readOnly && (
        <div className="flex justify-center pt-1">
          <Button
            type="submit"
            variant="action"
            disabled={isSubmitting || isChecking}
            className="px-10 py-1.5 text-xs font-medium"
          >
            {isChecking ? "Checking..." : isSubmitting ? "Saving..." : "Save"}
          </Button>
        </div>
      )}

      {/* Duplicate Warning Dialog */}
      <DuplicateWarningDialog
        open={showDuplicateDialog}
        onOpenChange={setShowDuplicateDialog}
        duplicates={duplicates}
        onProceed={handleProceedWithDuplicates}
        onCancel={handleCancelDuplicates}
        isSubmitting={isSubmitting}
      />

      {/* Close Lead Dialog */}
      {selectedCloseType && (
        <CloseLeadDialog
          open={showCloseDialog}
          onOpenChange={setShowCloseDialog}
          closeType={selectedCloseType}
          onConfirm={handleCloseConfirm}
          isLoading={isSubmitting}
        />
      )}

      {/* Job Archive Dialog (Sold → Assigned transition) */}
      <JobArchiveDialog
        open={showArchiveDialog}
        onOpenChange={setShowArchiveDialog}
        jobNumber={lead?.jobNumber}
        onConfirm={handleArchiveConfirm}
        isLoading={isSubmitting}
      />
    </form>
  );
}
